import {useTranslation} from '@/translations/i18n'
import {UserContractualDataFormValidationSchema, UserExtended} from '@/features/users/types'
import {FC} from 'react'
import {useUserEditModeView} from '@/features/users/hooks/useUserEditModeView'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {UserContractualDataForm} from '@/features/users/components/user-contractual-data-form/UserContractualDataForm'
import {UserDetailsEditButtons} from '@/features/users/components/user-details-edit-buttons/UserDetailsEditButtons'
import {
    contractTypeToLabel,
    getUserDirtyFieldsFromFormValues,
    remapUserToDefaultValues,
    remapUserToPayload
} from '@/features/users/utils'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CheckIcon} from '@/components/ui/icon/Icon'
import {DetailsField, DetailsFieldsGrid, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {DetailsInformationObservedField} from '@/components/commons/details-information-observed-field/DetailsInformationObservedField'
import {z} from 'zod'
import {formatCurrency} from '@/utilities/helpers'
import {zodResolver} from '@hookform/resolvers/zod'
import {useUserFormSubmission} from '@/features/users/hooks/useUserFormSubmission'
import {useUpdateUser} from '@/features/users/queries/useUpdateUser'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {Text} from '@/components/ui/text/Text'

interface UserDetailsInformationContractualDataProps {
    user: UserExtended
}

type UserContractualDataFormValidationKey = keyof z.infer<typeof UserContractualDataFormValidationSchema>

export const UserDetailsInformationContractualData: FC<UserDetailsInformationContractualDataProps> = ({user}) => {
    const form = useForm<UserContractualDataFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserContractualDataFormValidationSchema),
        defaultValues: UserContractualDataFormValidationSchema.parse(remapUserToDefaultValues(user))
    })
    return (
        <FormProvider {...form}>
            <UserDetailsInformationContractualDataContent user={user} />
        </FormProvider>
    )
}

const UserDetailsInformationContractualDataContent = ({user}: {user: UserExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = useUserEditModeView('contractual-data')
    const form = useFormContext<UserContractualDataFormValidationSchema>()
    const setIsErrorBannerVisible = useUserDetailsStore(state => state.setIsErrorBannerVisible)
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useUserFormSubmission({
        formSchema: UserContractualDataFormValidationSchema
    })

    // mutation
    const updateUser = useUpdateUser({
        urlParams: {userId: user.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getUserDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapUserToPayload(dirtyFormValues)
            return updateUser.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('users:contractual_data')}
            isDisabled={isDisabledView}
            headingEndSlot={
                <UserDetailsEditButtons
                    groupName="contractual-data"
                    isConfirmDisabled={!form.formState.isDirty}
                    onCancel={form.reset}
                    onConfirm={handleSubmit}
                />
            }
        >
            {isEditModeEnabled ? (
                <UserContractualDataForm />
            ) : (
                <DetailsFieldsGrid>
                    {user.contractType && (
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('users:contract_type')}
                            </Text>
                            <Text weight="500" size="textMd">
                                {t(contractTypeToLabel[user.contractType])}
                            </Text>
                        </DetailsField>
                    )}

                    {user.contractType != 'employee' && (
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {user.contractType == 'consultant'
                                    ? t('users:single_amount')
                                    : t('users:monthly_amount')}
                            </Text>
                            <DetailsInformationObservedField<UserContractualDataFormValidationKey>
                                name="amount"
                                fieldsToUpdate={user.fieldsToUpdate}
                                hideIcons={['success']}
                                value={
                                    user.amount && (
                                        <Text weight="500" size="textMd">
                                            {formatCurrency(user.amount ?? 0)}
                                        </Text>
                                    )
                                }
                            />
                        </DetailsField>
                    )}

                    {user.contractType != 'employee' && (
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('commons:hours_per_week')}
                            </Text>
                            <DetailsInformationObservedField<UserContractualDataFormValidationKey>
                                name="weeklyHours"
                                fieldsToUpdate={user.fieldsToUpdate}
                                hideIcons={['success']}
                                value={
                                    user.weeklyHours && (
                                        <Text weight="500" size="textMd">
                                            {user.weeklyHours}
                                        </Text>
                                    )
                                }
                            />
                        </DetailsField>
                    )}

                    {user.contractType != 'employee' && (
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('commons:vat_number')}
                            </Text>
                            <Flexbox gap={2}>
                                <Text weight="500" size="textMd">
                                    {user.vatNumber ?? '-'}
                                </Text>
                                {user.raExempt && (
                                    <Text palette="neutral" shade="500" size="textSm">
                                        <Flexbox gap={1} align="center">
                                            <CheckIcon size={14} />
                                            {t('users:ra_exempt')}
                                        </Flexbox>
                                    </Text>
                                )}
                            </Flexbox>
                        </DetailsField>
                    )}

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:bank_transfer')}
                        </Text>
                        <Text weight="500" size="textMd">
                            {user.iban ? t('users:iban_details', {value: user.iban}) : '-'}
                        </Text>
                    </DetailsField>
                </DetailsFieldsGrid>
            )}
        </DetailsFieldsSection>
    )
}
